import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HelpCircle, Phone, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../common';

/**
 * 개인정보 수집·이용 동의 약관 전문
 * 학생 가입 시(ky 서비스) 실제로 동의받는 문안과 동일.
 */
const PrivacyConsentContent = () => (
  <div className="space-y-4 text-sm leading-relaxed text-gray-700">
    <h3 className="text-center font-semibold">개인정보 수집 및 이용에 대한 안내</h3>
    <p>
      학원 입학 및 교육서비스 제공·안내 등을 위하여, 본 학원은 학생의 개인정보를 수집·이용하거나 제3자에게 제공하고자 합니다. 단, 만 14세 미만 학생의 경우에는 법정대리인(학부모 등)의 동의를, 만 14세 이상 학생의 경우에는 학생 본인과 학부모(법정대리인)의 공동 동의를 받아 진행함을 원칙으로 합니다.
      (본 동의서는 [개인정보 보호법] 제15조 제1항 제1호, 제17조 제1항 제1호, 제21조, 제22조 제3항 및 제5항, 제24조 제1항 제1호에 근거합니다.)
    </p>

    <h4 className="font-semibold">1. 개인정보 수집·이용 목적</h4>
    <p>본 학원은 다음의 목적으로 귀하의 개인정보를 수집·이용 또는 제3자에게 제공할 수 있습니다.</p>
    <ul className="list-disc pl-5">
      <li>교육서비스 제공 및 안내</li>
      <li>학원 홍보·마케팅 및 광고 활용</li>
      <li>시설 안전 관리</li>
    </ul>

    <h4 className="font-semibold">2. 수집·이용할 개인정보 항목</h4>
    <ul className="list-disc pl-5">
      <li>개인식별정보: 학생 성명, 생년월일, 가상계좌번호, 학교, 학년, 집전화번호, 휴대전화번호, 집주소, 이메일주소, 성적표, FAX 번호, 법정대리인(학부모) 정보(만 14세 미만 학생의 경우)</li>
      <li>교육 관련 정보: 교육서비스 이용기록, 학생의 학습 평가 정보(문제 정오답 여부, 시험 및 평가 결과, 학습 성과 등), 수강료 납부기록, 수강료 반환정보(학부모 성명, 은행계좌번호)</li>
      <li>세금 및 증빙 관련 정보: 국세청 현금영수증 발급요청 정보(학부모 휴대전화번호 또는 현금영수증 카드번호, 사업자등록번호)</li>
      <li>기타: CCTV 촬영 정보</li>
    </ul>

    <h4 className="font-semibold">3. 개인정보의 보유·이용 기간 및 파기</h4>
    <p>
      수집된 개인정보는 해당일로부터 최대 3년간 보관되며, 수집·이용 및 제공 목적이 달성된 경우 [개인정보 보호법] 제21조에 따라 지체 없이 파기됩니다.
      다만, 학원 수업 중단 후에는 내부 방침 및 상기 목적(사고 조사, 분쟁 해결, 민원 처리, 법령상 의무 이행 등)과 관련하여 필요한 범위 내에서 보유·이용될 수 있습니다.
    </p>

    <h4 className="font-semibold">4. 동의를 거부할 권리 및 동의 거부 시 불이익</h4>
    <p>귀하는 개인정보 제공에 대한 동의를 거부할 권리가 있으며, 동의 후에도 언제든지 철회할 수 있습니다. 다만, 동의하지 않으실 경우 교육서비스 제공·안내 등 관련 혜택이나 편의를 제공받지 못할 수 있습니다.</p>

    <h4 className="font-semibold">5. 동의 대상 및 추가 동의 사항</h4>
    <ul className="list-disc pl-5">
      <li><strong>(만 14세 이상 학생의 경우)</strong> 본인은 학생으로서, 본 학원에 의해 내 개인정보가 수집·이용·제공되는 것에 대해, 교육 관련 정보(교육서비스 이용기록, 학습 평가 정보, 수강료 납부기록 등)가 학부모(법정대리인)에게도 제공될 수 있음을 인지하고, 이에 대해 학부모(법정대리인)와 공동으로 동의함을 확인합니다.</li>
      <li><strong>(만 14세 미만 학생의 경우)</strong> 만 14세 미만 학생은 개인정보 처리에 관한 법적 동의 능력이 없으므로, 해당 학생의 개인정보 수집·이용·제공은 법정대리인(학부모)의 동의를 통해 진행됩니다. 이에 법정대리인(학부모)은 만 14세 미만 학생의 개인정보, 교육 관련 정보가 본 학원에 의해 수집·이용·제공되는 것에 대해 동의함을 확인합니다.</li>
    </ul>

    <p>위와 같이 [개인정보 보호법] 제15조, 제17조, 제21조, 제22조 및 제24조에 근거하여, 본 학원이 학생의 개인정보를 수집·이용·제공하는 것에 동의합니다.</p>
  </div>
);

// 가입 시점에 개인정보 수집·이용 동의를 받으므로 전 회원을 '동의완료'로 간주하고
// 가입일(created_at)을 동의일로 표기한다.
// 추후 백엔드에 실제 동의 여부/일자 필드가 추가되면 이 파생 함수만 교체하면 된다.
const deriveConsent = (student) => ({
  agreed: true,
  date: student.created_at,
});

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const THEAD_CLASS =
  'px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap';

/**
 * 회원 현황 모달
 * 전체 회원을 조회해 학생 정보 현황을 테이블로 한눈에 표출한다.
 */
function MemberStatusModal({ isOpen, onClose }) {
  const { getAccessToken, userId, userType } = useAuth();
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);

  // 모달이 닫히면 약관 보조 모달도 함께 닫는다.
  useEffect(() => {
    if (!isOpen) setShowTerms(false);
  }, [isOpen]);

  // 모달이 열릴 때 전체 회원을 조회한다. (페이지 목록과 달리 전 회원 수집)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchAllMembers = async () => {
      setIsLoading(true);
      setError('');
      try {
        const token = await getAccessToken();
        if (!token) {
          setError('인증이 만료되었습니다. 다시 로그인해 주세요.');
          return;
        }

        const PER_PAGE = 500;
        const MAX_PAGES = 50; // 무한 루프 방지 안전장치
        let collected = [];
        let pageNum = 1;
        let totalPages = 1;

        do {
          const params = new URLSearchParams({ page: pageNum, per_page: PER_PAGE });
          let url;
          if (userType === 'manager') {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          } else if (userType === 'teacher') {
            params.append('teacher_id', userId);
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
          } else {
            break;
          }

          const response = await fetch(url, {
            headers: { Authorization: token, 'Content-Type': 'application/json' },
          });

          if (response.status === 404) break; // 데이터 없음
          if (!response.ok) throw new Error('회원 목록을 불러오지 못했습니다.');

          const result = await response.json();
          collected = collected.concat(result.data || []);
          totalPages = result.total_pages || 1;
          pageNum += 1;
        } while (pageNum <= totalPages && pageNum <= MAX_PAGES);

        if (cancelled) return;
        setStudents(collected);
      } catch (e) {
        if (cancelled) return;
        console.error('회원 현황 조회 실패:', e);
        setError('회원 목록을 불러오지 못했습니다.');
        setStudents([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAllMembers();
    return () => {
      cancelled = true;
    };
  }, [isOpen, getAccessToken, userId, userType]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="회원 현황"
        size="max"
        maxHeight="90vh"
        closeOnEsc={!showTerms} // 약관 모달이 열려 있으면 ESC는 약관만 닫는다.
      >
        {isLoading ? (
          <div className="py-16 text-center text-gray-500">로딩 중...</div>
        ) : error ? (
          <div className="py-16 text-center text-red-500">{error}</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center text-gray-400">회원이 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-3 flex items-center">
              <p className="text-sm text-gray-600">
                총 <span className="font-semibold text-primary">{students.length}</span>명
              </p>
            </div>

            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${THEAD_CLASS} text-center`}>학생아이디</th>
                  <th className={`${THEAD_CLASS} text-left`}>학생명/이메일</th>
                  <th className={`${THEAD_CLASS} text-left`}>학교/학년</th>
                  <th className={`${THEAD_CLASS} text-left`}>연락처/학부모</th>
                  <th className={`${THEAD_CLASS} text-left`}>가입 경로</th>
                  <th className={`${THEAD_CLASS} text-left`}>
                    <div className="flex items-center gap-1">
                      <span>가입일/개인정보제공동의</span>
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="text-gray-400 transition-colors hover:text-primary"
                        aria-label="개인정보제공동의 약관 보기"
                        title="개인정보제공동의 약관 보기"
                      >
                        <HelpCircle size={14} />
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {students.map((student) => {
                  const consent = deriveConsent(student);
                  return (
                    <tr
                      key={student.student_id}
                      className="border-b border-gray-100 transition-colors duration-150 hover:bg-primary/10"
                    >
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex justify-center">
                          <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white">
                            {student.student_id}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                        <div className="text-xs text-gray-500">{student.student_email}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="text-sm text-gray-900">{student.school_name || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {student.school_grade ? `${student.school_grade}학년` : '-'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <Phone className="size-3 text-gray-400" />
                          <span>{student.mobile_number || '-'}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          학부모: {student.parent_mobile_number || '-'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <span className="text-sm text-gray-900">{student.acq_channel || '-'}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="text-sm text-gray-900">{formatDate(consent.date)}</div>
                        <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-600">
                          <ShieldCheck className="size-3" />
                          동의완료
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* 개인정보 수집·이용 동의 약관 (보조 모달) */}
      <Modal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        title="개인정보 수집 및 이용 동의"
        size="lg"
        maxHeight="85vh"
        zIndex={9999985} // 회원 현황 모달(기본 9999980) 위로 노출
      >
        <PrivacyConsentContent />
      </Modal>
    </>
  );
}

MemberStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MemberStatusModal;
